import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { BodyColorService } from 'src/app/services/body-color/body-color.service';
import { BodyColorModalService } from 'src/app/services/body-color-modal/body-color-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-body-color',
  templateUrl: './add-edit-body-color.component.html',
  styleUrls: ['./add-edit-body-color.component.scss']
})
export class AddEditBodyColorComponent extends DestructorComponent implements OnInit {

  @Output() bodyColorAdded = new EventEmitter<void>();
  @Output() bodyColorUpdated = new EventEmitter<void>();

  bodyColorForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedBodyColor: any = null;
  parentBodyColors: any[] = [];
  isLoadingParents = false;

  constructor(
    private fb: FormBuilder,
    private _bodyColorService: BodyColorService,
    private _bodyColorModalService: BodyColorModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadParentBodyColors();
  }

  private initializeForm(): void {
    this.bodyColorForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      parent: ['']
    });
  }

  // Getter methods for form controls
  get name() { return this.bodyColorForm.get('name'); }
  get parent() { return this.bodyColorForm.get('parent'); }

  private loadParentBodyColors(): void {
    this.isLoadingParents = true;
    this.subscriptions.push(
      this._bodyColorService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.parentBodyColors = response.data.data || response.data || [];
          } else {
            this.parentBodyColors = [];
          }
          this.isLoadingParents = false;
        },
        error: (error) => {
          console.error('Error loading parent body colors:', error);
          this.parentBodyColors = [];
          this.isLoadingParents = false;
        }
      })
    );
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_body_color_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceBodyColor = this._bodyColorModalService.getSelectedBodyColor();
        if (serviceBodyColor) {
          this.isEditMode = true;
          this.selectedBodyColor = serviceBodyColor;
          this.populateForm(this.selectedBodyColor);
        } else {
          this.isEditMode = false;
          this.selectedBodyColor = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._bodyColorModalService.clearSelectedBodyColor();
        this.resetForm();
      });
    }
  }

  private populateForm(bodyColor: any): void {
    this.bodyColorForm.patchValue({
      name: bodyColor.name || '',
      parent: bodyColor.parent?._id || ''
    });
  }

  private resetForm(): void {
    this.bodyColorForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.bodyColorForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_body_color_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.bodyColorForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.bodyColorForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._bodyColorService.UpdateData(this.selectedBodyColor._id, submitData)
        : this._bodyColorService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Body color operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Body color has been successfully updated!"
                : "Body color has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.bodyColorUpdated.emit();
                } else {
                  this.bodyColorAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Body color operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.bodyColorForm);
    }
  }
}
